import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { HiOutlineQuestionMarkCircle } from 'react-icons/hi';

// Small "?" icon that opens a positioned help popover on click. Used next to a label/stat to
// explain what it means or how it's calculated. Tracks scroll/resize while open so the popover
// stays anchored under the icon instead of drifting on page scroll (portal-rendered to
// document.body with position:fixed so it isn't clipped by an overflow:hidden ancestor).
const HelpTooltip = ({ text, size = 18, className = '' }) => {
    const [show, setShow] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const iconRef = useRef(null);
    const tipRef = useRef(null);

    const updatePosition = () => {
        if (iconRef.current) {
            const rect = iconRef.current.getBoundingClientRect();
            const tip = tipRef.current?.getBoundingClientRect();
            const margin = 8;
            let top = rect.bottom + 6;
            let left = rect.left;
            if (tip) {
                // Not enough room on the right → open towards the left; not enough below → open above.
                if (left + tip.width > window.innerWidth - margin) left = rect.right - tip.width;
                left = Math.max(margin, Math.min(left, window.innerWidth - tip.width - margin));
                if (top + tip.height > window.innerHeight - margin && rect.top - tip.height - 6 >= margin) {
                    top = rect.top - tip.height - 6;
                }
            }
            setPos({ top, left });
        }
    };

    // Re-measure once the popover is in the DOM so its real size decides the side.
    useLayoutEffect(() => {
        if (show) updatePosition();
    }, [show]);

    useEffect(() => {
        if (!show) return undefined;
        updatePosition();
        // capture:true so scrolling inside any scrollable ancestor (not just the window) also
        // repositions the popover, not just a top-level page scroll.
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);
        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [show]);

    const toggle = (e) => {
        e.preventDefault();
        e.stopPropagation();
        updatePosition();
        setShow((v) => !v);
    };

    return (
        <span className={className}>
            <span
                ref={iconRef}
                role="button"
                tabIndex={0}
                aria-label="Show help"
                onClick={toggle}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') toggle(e);
                }}
                style={{ cursor: 'pointer', marginLeft: '4px', verticalAlign: 'middle', display: 'inline-flex' }}
            >
                <HiOutlineQuestionMarkCircle size={size} />
            </span>
            {show && createPortal(
                <>
                    <div
                        role="presentation"
                        style={{ position: 'fixed', inset: 0, zIndex: 1040 }}
                        onClick={() => setShow(false)}
                        onKeyDown={(e) => e.key === 'Escape' && setShow(false)}
                    />
                    <div
                        role="tooltip"
                        ref={tipRef}
                        className="border rounded shadow-sm bg-white p-3 position-fixed"
                        style={{
                            zIndex: 1050,
                            width: 'max-content',
                            maxWidth: 320,
                            top: pos.top,
                            left: pos.left,
                        }}
                    >
                        <button
                            type="button"
                            className="btn btn-link p-0 position-absolute"
                            style={{
                                top: 6,
                                right: 8,
                                fontSize: '1.25rem',
                                lineHeight: 1,
                                textDecoration: 'none',
                                color: '#6c757d',
                            }}
                            aria-label="Close"
                            onClick={() => setShow(false)}
                        >
                            ×
                        </button>
                        <div className="pe-3">{text}</div>
                    </div>
                </>,
                document.body
            )}
        </span>
    );
};

export default HelpTooltip;
